import { useState, useEffect, useCallback } from "react";
import { ArrowLeft, Crown, Wallet, Percent, BadgeCheck, Layers, Check, Lock } from "lucide-react";
import { useSession } from "@/context/SessionContext";

// STAGE 7 · M6 — Gold-Badge Business Plan Interface (client-side, localStorage
// onboarding state — zero DB collection bloat). Full-screen macro overlay.
const MILESTONES = [
  { key: "wallet", title: "Connect Creator Wallet", desc: "Link your Phantom / Solana payout address.", Icon: Wallet },
  { key: "rates", title: "Establish Base Credit Rates", desc: "Set your studio's per-run Credit pricing.", Icon: Percent },
  { key: "identity", title: "Verify Identity Profile", desc: "Confirm business identity for the Gold Badge.", Icon: BadgeCheck },
  { key: "ledger", title: "Deploy Multi-Token Ledger", desc: "Activate isolated multi-token tax tracking.", Icon: Layers },
];

export default function BusinessPlanInterface({ onBack }) {
  const { user } = useSession();
  const storeKey = `misteck_bizplan_checklist_${user?.user_id || "anon"}`;
  const [done, setDone] = useState({});

  useEffect(() => {
    try { setDone(JSON.parse(localStorage.getItem(storeKey) || "{}")); } catch { setDone({}); }
  }, [storeKey]);

  const toggle = useCallback((k) => {
    setDone((prev) => {
      const next = { ...prev, [k]: !prev[k] };
      try { localStorage.setItem(storeKey, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, [storeKey]);

  const completed = MILESTONES.filter((m) => done[m.key]).length;
  const allDone = completed === MILESTONES.length;

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="business-plan-interface">
      {/* Gold-Badge header */}
      <header className="shrink-0 px-4 pt-4 pb-4 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #D4AF37 0%, #F2C94C 45%, #B8860B 100%)" }}>
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-testid="bizplan-back-btn" className="w-10 h-10 rounded-full bg-black/15 border-2 border-black/40 grid place-items-center active:scale-95">
            <ArrowLeft size={18} className="text-black" />
          </button>
          <span className="w-11 h-11 rounded-xl bg-charcoal border-2 border-black grid place-items-center shadow-[3px_3px_0px_rgba(0,0,0,0.4)]">
            <Crown size={22} className="text-gold" />
          </span>
          <div className="leading-none">
            <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-black/70">Premium Tier</p>
            <h1 className="font-serif text-2xl text-black">Gold-Badge Business Plan</h1>
          </div>
        </div>
        <p className="mt-3 font-mono text-[10px] text-black/70 leading-relaxed">
          Unlock premium multiplier rates, multi-wallet business handles and corporate tax ledgers.
        </p>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 pb-28 space-y-4">
        {/* Progress */}
        <div className="rounded-2xl border-2 border-gold/40 bg-white/5 p-3.5" data-testid="bizplan-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] uppercase tracking-widest text-gold/80">Setup Checklist</span>
            <span className="font-serif text-lg text-gold" data-testid="bizplan-progress-count">{completed}/{MILESTONES.length}</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-black/40 overflow-hidden">
            <div className="h-full rounded-full bg-gold transition-[width] duration-500" style={{ width: `${(completed / MILESTONES.length) * 100}%` }} />
          </div>
        </div>

        {/* Checklist */}
        <div className="space-y-2.5" data-testid="bizplan-checklist">
          {MILESTONES.map((m) => {
            const on = !!done[m.key];
            const Icon = m.Icon;
            return (
              <button
                key={m.key}
                onClick={() => toggle(m.key)}
                data-testid={`bizplan-item-${m.key}`}
                aria-pressed={on}
                className={`w-full flex items-center gap-3 rounded-2xl border-2 p-3.5 text-left transition-colors ${
                  on ? "border-gold bg-gold/10" : "border-white/12 bg-white/[0.03]"}`}
              >
                <span className={`w-6 h-6 shrink-0 rounded-md border-2 grid place-items-center ${on ? "bg-gold border-gold" : "border-white/30"}`} data-testid={`bizplan-check-${m.key}`}>
                  {on && <Check size={15} className="text-black" strokeWidth={3.5} />}
                </span>
                <Icon size={20} className={on ? "text-gold" : "text-white/50"} />
                <div className="flex-1 min-w-0">
                  <p className={`font-sans font-bold text-[15px] leading-tight ${on ? "text-white" : "text-white/80"}`}>{m.title}</p>
                  <p className="font-mono text-[10px] text-white/45 leading-snug mt-0.5">{m.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Activate CTA (coming soon — no live checkout wired) */}
        <button disabled data-testid="bizplan-activate-btn"
          className={`w-full flex items-center justify-center gap-2 border-2 font-mono text-[11px] font-black uppercase tracking-widest py-3.5 rounded-full cursor-not-allowed ${
            allDone ? "bg-gold text-black border-charcoal opacity-95" : "bg-white/5 text-white/40 border-white/15"}`}>
          <Lock size={14} /> {allDone ? "Activate Gold Badge · Coming Soon" : "Complete checklist to activate"}
        </button>
      </div>
    </div>
  );
}
